import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  MessageSquare, Phone, Users, Clock, TrendingUp,
  Download, RefreshCw, Search, Filter, PhoneCall,
  ArrowUpDown, ChevronDown, ChevronUp, AlertTriangle,
  Signal, UserCheck, Zap, Eye, X
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";

const AdminUsage = () => {
  const [usageData, setUsageData] = useState(null);
  const [userUsage, setUserUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateRange, setDateRange] = useState("30");
  const [sortField, setSortField] = useState("sms_used");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedUser, setSelectedUser] = useState(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    fetchUsageData();
  }, [dateRange]);

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const [summaryRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/usage-summary?days=${dateRange}`, getAuthConfig()).catch(e => ({ data: null, error: e })),
        axios.get(`${API}/admin/usage-users`, getAuthConfig()).catch(e => ({ data: { users: [] }, error: e }))
      ]);
      
      if (summaryRes.data) setUsageData(summaryRes.data);
      setUserUsage(usersRes.data?.users || []);
      
      if (summaryRes.error && usersRes.error) {
        if (summaryRes.error.response?.status !== 401) {
          toast.error("Failed to load usage data");
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setLoading(false);
    }
  };

  const reconcileAll = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/admin/usage-reconcile-all`, {}, getAuthConfig());
      toast.success(`Synced ${res.data.reconciled_users} users for ${res.data.month}`);
      fetchUsageData();
    } catch (error) {
      toast.error("Failed to reconcile usage data");
    } finally {
      setReconciling(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let result = userUsage.filter(user => {
      const matchesSearch = !searchTerm || 
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.business_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPlan = filterPlan === "all" || user.subscription_plan === filterPlan;
      const matchesStatus = filterStatus === "all" || user.subscription_status === filterStatus;
      return matchesSearch && matchesPlan && matchesStatus;
    });

    result.sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });

    return result;
  }, [userUsage, searchTerm, filterPlan, filterStatus, sortField, sortDir]);

  const uniquePlans = useMemo(() => {
    const plans = new Set(userUsage.map(u => u.subscription_plan).filter(Boolean));
    return Array.from(plans).sort();
  }, [userUsage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const exportCSV = () => {
    const headers = ["Email", "Name", "Business", "Plan", "Status", "SMS Sent", "SMS Received", "SMS Total", "SMS Limit", "Minutes Used", "Minutes Limit", "Calls Made", "Calls Received", "Lines", "Lines Limit", "Team", "Team Limit", "Technicians"];
    const rows = filteredUsers.map(u => [
      u.email,
      u.name || "-",
      u.business_name || "-",
      u.subscription_plan || "starter",
      u.subscription_status || "active",
      u.sms_sent || 0,
      u.sms_received || 0,
      u.sms_used || 0,
      u.sms_limit || 0,
      u.minutes_used || 0,
      u.minutes_limit || 0,
      u.calls_made || 0,
      u.calls_received || 0,
      u.lines_count || 0,
      u.lines_limit || 0,
      u.team_count || 0,
      u.team_limit || 0,
      u.tech_count || 0
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usage_report_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  const getUsagePercent = (used, limit) => {
    if (!limit || limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getBarColor = (percent) => {
    if (percent >= 90) return "bg-red-500";
    if (percent >= 75) return "bg-amber-500";
    if (percent >= 50) return "bg-blue-500";
    return "bg-emerald-500";
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case "premium": return "bg-purple-500/15 text-purple-400 border-purple-500/30";
      case "professional": case "pro": return "bg-blue-500/15 text-blue-400 border-blue-500/30";
      case "standard": return "bg-teal-500/15 text-teal-400 border-teal-500/30";
      case "starter": return "bg-slate-500/15 text-slate-400 border-slate-500/30";
      default: return "bg-slate-500/15 text-slate-400 border-slate-500/30";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-emerald-500/15 text-emerald-400";
      case "frozen": return "bg-blue-500/15 text-blue-400";
      case "suspended": return "bg-red-500/15 text-red-400";
      case "trialing": return "bg-amber-500/15 text-amber-400";
      default: return "bg-slate-500/15 text-slate-400";
    }
  };

  const SortHeader = ({ label, field, className = "" }) => (
    <th
      className={`text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors ${className}`}
      onClick={() => handleSort(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1.5">
        {label}
        {sortField === field ? (
          sortDir === "desc" ? <ChevronDown className="w-3.5 h-3.5 text-blue-400" /> : <ChevronUp className="w-3.5 h-3.5 text-blue-400" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </div>
    </th>
  );

  const UsageBar = ({ used, limit, showLabel = true }) => {
    const percent = getUsagePercent(used, limit);
    const isUnlimited = limit === -1;
    return (
      <div className="flex items-center gap-2.5">
        <div className="w-24 h-2 bg-muted/80 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${isUnlimited ? "bg-slate-400" : getBarColor(percent)}`}
            style={{ width: `${isUnlimited ? 0 : percent}%` }}
          />
        </div>
        {showLabel && (
          <span className={`text-xs font-mono tabular-nums ${percent >= 90 ? "text-red-400 font-semibold" : "text-muted-foreground"}`}>
            {used}{isUnlimited ? " / ∞" : ` / ${limit}`}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-usage-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="usage-page-title">Usage Tracking</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Monitor resource consumption across {usageData?.total_users || "all"} accounts
            {usageData?.period && <span className="ml-1 text-xs">({usageData.period})</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px] bg-card border-border text-foreground h-9 text-sm" data-testid="date-range-select">
              <Clock className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchUsageData} className="border-border text-foreground h-9" data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={reconcileAll}
            disabled={reconciling}
            className="border-border text-foreground h-9"
            data-testid="reconcile-all-btn"
          >
            {reconciling ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5 mr-1.5" />}
            {reconciling ? "Syncing..." : "Sync All"}
          </Button>
          <Button size="sm" onClick={exportCSV} className="bg-[#0046BE] hover:bg-[#003da6] text-white h-9" data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-border animate-pulse">
              <CardContent className="p-5"><div className="h-16 bg-muted rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : usageData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="usage-summary-cards">
            <Card className="border-border bg-card overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent" />
              <CardContent className="p-5 relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total SMS</p>
                    <p className="text-3xl font-bold text-foreground mt-1 tabular-nums" data-testid="total-sms-value">
                      {(usageData.total_sms || 0).toLocaleString()}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-400" /> {usageData.total_sms_sent || 0} sent
                      </span>
                      <span>{usageData.total_sms_received || 0} received</span>
                    </div>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
                    <MessageSquare className="w-5 h-5 text-violet-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent" />
              <CardContent className="p-5 relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Call Minutes</p>
                    <p className="text-3xl font-bold text-foreground mt-1 tabular-nums" data-testid="total-minutes-value">
                      {(usageData.total_minutes || 0).toLocaleString()}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span>{usageData.total_calls_made || 0} outbound</span>
                      <span>{usageData.total_calls_received || 0} inbound</span>
                    </div>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-orange-500/15 flex items-center justify-center shrink-0">
                    <PhoneCall className="w-5 h-5 text-orange-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent" />
              <CardContent className="p-5 relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Phone Lines</p>
                    <p className="text-3xl font-bold text-foreground mt-1 tabular-nums" data-testid="total-lines-value">
                      {usageData.total_lines || 0}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Signal className="w-3 h-3 text-emerald-400" /> {usageData.total_active_lines || 0} active
                      </span>
                    </div>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent" />
              <CardContent className="p-5 relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">At Limit</p>
                    <p className="text-3xl font-bold text-foreground mt-1 tabular-nums" data-testid="at-limit-value">
                      {usageData.users_at_limit || 0}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {usageData.users_at_limit > 0 ? (
                        <span className="flex items-center gap-1 text-amber-400">
                          <AlertTriangle className="w-3 h-3" /> Users need attention
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-emerald-400">
                          <UserCheck className="w-3 h-3" /> All within limits
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Plan Distribution */}
          {usageData.plan_distribution && Object.keys(usageData.plan_distribution).length > 0 && (
            <Card className="border-border bg-card" data-testid="plan-distribution-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold text-foreground">Plan Distribution</p>
                  <span className="text-xs text-muted-foreground">{usageData.total_users} total accounts</span>
                </div>
                <div className="flex gap-2 h-3 rounded-full overflow-hidden bg-muted/50">
                  {Object.entries(usageData.plan_distribution).map(([plan, count]) => {
                    const pct = (count / usageData.total_users) * 100;
                    const colors = {
                      premium: "bg-purple-500",
                      professional: "bg-blue-500",
                      pro: "bg-blue-500",
                      standard: "bg-teal-500",
                      starter: "bg-slate-400",
                    };
                    return pct > 0 ? (
                      <div
                        key={plan}
                        className={`${colors[plan] || "bg-slate-400"} transition-all duration-500`}
                        style={{ width: `${pct}%` }}
                        title={`${plan}: ${count} (${pct.toFixed(0)}%)`}
                      />
                    ) : null;
                  })}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3">
                  {Object.entries(usageData.plan_distribution).map(([plan, count]) => (
                    <div key={plan} className="flex items-center gap-2 text-xs">
                      <div className={`w-2.5 h-2.5 rounded-full ${
                        plan === "premium" ? "bg-purple-500" :
                        plan === "professional" || plan === "pro" ? "bg-blue-500" :
                        plan === "standard" ? "bg-teal-500" : "bg-slate-400"
                      }`} />
                      <span className="text-muted-foreground capitalize">{plan}</span>
                      <span className="font-semibold text-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* User Usage Table */}
      <Card className="border-border bg-card" data-testid="user-usage-table">
        <CardHeader className="pb-3">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-foreground text-base font-['Manrope']">User Resource Usage</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {filteredUsers.length} of {userUsage.length} users — per subscription billing cycle
                {(searchTerm || filterPlan !== "all" || filterStatus !== "all") && " (filtered)"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search email, name, business..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-background border-border text-foreground pl-8 pr-8 h-9 text-sm w-56"
                  data-testid="search-users-input"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-[130px] bg-background border-border text-foreground h-9 text-sm" data-testid="filter-plan-select">
                  <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All Plans</SelectItem>
                  {uniquePlans.map(plan => (
                    <SelectItem key={plan} value={plan} className="capitalize">{plan}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[130px] bg-background border-border text-foreground h-9 text-sm" data-testid="filter-status-select">
                  <Zap className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="frozen">Frozen</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="usage-table">
              <thead>
                <tr className="border-y border-border bg-muted/30">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground">User</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground">Plan</th>
                  <SortHeader label="SMS" field="sms_used" />
                  <SortHeader label="Minutes" field="minutes_used" />
                  <SortHeader label="Lines" field="lines_count" />
                  <SortHeader label="Team" field="team_count" />
                  <th className="text-center text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground w-16"></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const smsPercent = getUsagePercent(user.sms_used || 0, user.sms_limit);
                  const minPercent = getUsagePercent(user.minutes_used || 0, user.minutes_limit);
                  
                  return (
                    <tr
                      key={user.user_id}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors group"
                      data-testid={`user-row-${user.user_id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="min-w-[180px]">
                          <p className="text-sm font-medium text-foreground truncate">{user.email}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {user.name || user.business_name || "—"}
                          </p>
                          {user.billing_period_start && (
                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                              Since {format(new Date(user.billing_period_start), "MMM d")}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          <Badge className={`text-[10px] px-2 py-0.5 font-medium border ${getPlanColor(user.subscription_plan)}`}>
                            {user.subscription_plan || "starter"}
                          </Badge>
                          <Badge className={`text-[10px] px-1.5 py-0 font-normal ${getStatusColor(user.subscription_status)}`}>
                            {user.subscription_status || "active"}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <UsageBar used={user.sms_used || 0} limit={user.sms_limit} />
                        {smsPercent >= 75 && (
                          <span className="text-[10px] text-amber-400 mt-0.5 block">{Math.round(smsPercent)}% used</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <UsageBar used={user.minutes_used || 0} limit={user.minutes_limit} />
                        {minPercent >= 75 && (
                          <span className="text-[10px] text-amber-400 mt-0.5 block">{Math.round(minPercent)}% used</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-foreground font-mono tabular-nums">
                          {user.lines_count || 0}
                          <span className="text-muted-foreground"> / {user.lines_limit}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-foreground font-mono tabular-nums">
                          {user.team_count || 0}
                          <span className="text-muted-foreground"> / {user.team_limit}</span>
                        </span>
                        {user.tech_count > 0 && (
                          <span className="text-[10px] text-muted-foreground block mt-0.5">+{user.tech_count} techs</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setSelectedUser(user)}
                          data-testid={`view-user-${user.user_id}`}
                        >
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="no-users-found">
                <Users className="w-10 h-10 text-muted-foreground/40 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No users found</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
    </div>
  );
};

const UserDetailModal = ({ user, onClose }) => {
  if (!user) return null;

  const getUsagePercent = (used, limit) => {
    if (!limit || limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getBarColor = (percent) => {
    if (percent >= 90) return "bg-red-500";
    if (percent >= 75) return "bg-amber-500";
    if (percent >= 50) return "bg-blue-500";
    return "bg-emerald-500";
  };

  const resources = [
    {
      label: "SMS",
      icon: MessageSquare,
      used: user.sms_used || 0,
      limit: user.sms_limit,
      color: "text-violet-400",
      bg: "bg-violet-500/15",
      details: [
        { label: "Sent", value: user.sms_sent || 0 },
        { label: "Received", value: user.sms_received || 0 },
      ]
    },
    {
      label: "Minutes",
      icon: PhoneCall,
      used: user.minutes_used || 0,
      limit: user.minutes_limit,
      color: "text-orange-400",
      bg: "bg-orange-500/15",
      details: [
        { label: "Calls Made", value: user.calls_made || 0 },
        { label: "Calls Received", value: user.calls_received || 0 },
      ]
    },
    {
      label: "Phone Lines",
      icon: Phone,
      used: user.lines_count || 0,
      limit: user.lines_limit,
      color: "text-cyan-400",
      bg: "bg-cyan-500/15",
      details: []
    },
    {
      label: "Team Members",
      icon: Users,
      used: user.team_count || 0,
      limit: user.team_limit,
      color: "text-blue-400",
      bg: "bg-blue-500/15",
      details: [
        { label: "Technicians", value: user.tech_count || 0 },
      ]
    },
  ];

  return (
    <Dialog open={!!user} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="user-detail-modal" aria-describedby="user-detail-desc">
        <DialogHeader>
          <DialogTitle className="text-foreground font-['Manrope'] text-lg">User Usage Details</DialogTitle>
          <p id="user-detail-desc" className="sr-only">Detailed resource usage breakdown for this user</p>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* User info header */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center text-sm font-bold text-foreground">
              {(user.email?.[0] || "?").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate" data-testid="modal-user-email">{user.email}</p>
              <p className="text-xs text-muted-foreground truncate">
                {user.name || user.business_name || "No business name"}
              </p>
              {user.billing_period_start && (
                <p className="text-[10px] text-muted-foreground/70 mt-0.5" data-testid="billing-period-label">
                  Billing: {format(new Date(user.billing_period_start), "MMM d")} — {user.billing_period_end ? format(new Date(user.billing_period_end), "MMM d, yyyy") : "ongoing"}
                </p>
              )}
            </div>
            <Badge className={`text-[10px] px-2 py-0.5 border ${
              user.subscription_plan === "premium" ? "bg-purple-500/15 text-purple-400 border-purple-500/30" :
              user.subscription_plan === "professional" || user.subscription_plan === "pro" ? "bg-blue-500/15 text-blue-400 border-blue-500/30" :
              "bg-slate-500/15 text-slate-400 border-slate-500/30"
            }`}>
              {user.subscription_plan}
            </Badge>
          </div>

          {/* Resource breakdown */}
          <div className="grid grid-cols-1 gap-3">
            {resources.map(res => {
              const pct = getUsagePercent(res.used, res.limit);
              return (
                <div key={res.label} className="p-3 rounded-lg border border-border bg-background">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg ${res.bg} flex items-center justify-center`}>
                        <res.icon className={`w-3.5 h-3.5 ${res.color}`} />
                      </div>
                      <span className="text-sm font-medium text-foreground">{res.label}</span>
                    </div>
                    <span className="text-xs font-mono tabular-nums text-muted-foreground">
                      {res.used} / {res.limit === -1 ? "∞" : res.limit}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted/80 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getBarColor(pct)}`}
                      style={{ width: `${res.limit === -1 ? 0 : pct}%` }}
                    />
                  </div>
                  {res.details.length > 0 && (
                    <div className="flex gap-4 mt-2">
                      {res.details.map(d => (
                        <span key={d.label} className="text-[11px] text-muted-foreground">
                          {d.label}: <span className="font-medium text-foreground">{d.value}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminUsage;
